import { capitalize, snakeCase, startCase } from 'lodash';

export function generateRandomString(length = 6): string {
  let result = '';
  // const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export const removeUnderscoresFromString = (input: string, capitalizeFirstWordFirstLetter = true) => {
  if (!input) {
    return '';
  }
  // Split the string by underscores
  const words = input.split('_');
  // Capitalize the first letter of the first word only if the flag is true
  if (capitalizeFirstWordFirstLetter) words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  // Join the words with spaces
  return words.join(' ');
};

export const capitalizeFirstLetter = (value: string) => {
  if (!value || typeof value !== 'string') return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const capitalizeOnlyFirstLetter = (value: string) => {
  if (!value || typeof value !== 'string') return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const convertToTitleCase = (value: string) => {
  if (!value) return '';
  return value
    ?.toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getPlural = (count: number, singularForm: string) => {
  if (count === 1 || count === -1) {
    return singularForm;
  }
  return singularForm + 's';
};

export function removeUnderscoreAndCapitalizeFirstLetters(inputString: string): string {
  if (!inputString || typeof inputString !== 'string') return '';
  const words = inputString.split('_');
  const formattedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  return formattedWords.join(' ');
}

/**
 * The one way to turn a name into avatar initials. Takes either a full name ("Jane Doe") or a
 * first and last name, and falls back to '?' so an avatar is never blank.
 */
export function getInitials(nameOrFirstName?: string | null, lastName?: string | null): string {
  const parts = `${nameOrFirstName ?? ''} ${lastName ?? ''}`.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }
  // A single token is usually a first name or a company name, so two letters read better than one.
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export const maskCharacters = (input: string, charactersToIgnore = 4) => {
  if (!input) return '';

  const regexPattern = new RegExp(`\\w(?=\\w{${charactersToIgnore}})`, 'g');
  return input.replace(regexPattern, 'X');
};

export function containsNumbers(inputString: string): boolean {
  const regex = /[0-9]/; // Regular expression to match any digit (0-9)

  // Use the test method to check if the input string contains any numbers
  return regex.test(inputString);
}

export function camelCaseToSentenceCase(input: string) {
  if (!input) return '';

  const isAllCaps = input?.toUpperCase() === input;

  if (isAllCaps) {
    return input; // Return the string as is if it's all caps
  }

  const snakeCaseString = snakeCase(input); // Convert camelCase to snake_case
  const words = snakeCaseString.split('_');

  const sentenceCase = words.map((word) => startCase(word)).join(' '); // Convert snake_case to sentence case
  return sentenceCase;
}

// Written in camelCase these lose their capitals ("companyId", "amountUsd"), so they are restored by name.
const CAMEL_CASE_ACRONYMS = new Set([
  'id',
  'usd',
  'ein',
  'api',
  'ach',
  'kyc',
  'url',
  'ot',
  'pto',
  'eor',
  'dba',
  'ap',
  'ar',
]);

/**
 * "employeeUserID" as "Employee user ID": split at each capital and lowercased after the first word,
 * with acronyms kept in capitals. camelCaseToSentenceCase capitalises every word instead.
 */
export function camelCaseToSentence(input: string): string {
  const words = input.match(/[A-Z]{2,}(?=[A-Z][a-z]|\d|$)|[A-Z]?[a-z]+\d*|[A-Z]+|\d+/g) ?? [];
  return words
    .map((word, index) => {
      if (CAMEL_CASE_ACRONYMS.has(word.toLowerCase()) || (word.length > 1 && word === word.toUpperCase())) {
        return word.toUpperCase();
      }
      return index === 0 ? capitalizeOnlyFirstLetter(word) : word.toLowerCase();
    })
    .join(' ');
}

export function checkIfEveryWordHasMoreThanGivenCharacterLength(inputString: string, characterLength = 1): boolean {
  const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'&-]*$/;

  // Split the string at more than one consecutive space
  const words = inputString?.trim()?.split(/\s+/);

  // Check if every word has two or more characters and no special characters
  return words.every((word) => word?.trim()?.length >= characterLength && regex.test(word));
}

export function checkIfEveryWordHasMoreThanOneCharacter(inputString: string) {
  // Check if every word has two or more characters and no special characters
  return checkIfEveryWordHasMoreThanGivenCharacterLength(inputString, 2);
}

//  function to handle encoding without double encoding
export function encodeEmail(email: string) {
  // Attempt to decode the email to see if it's already encoded
  // This will throw an error if the email is not properly encoded (e.g., a raw email)
  try {
    const decodedEmail = decodeURIComponent(email);
    // If decoding doesn't throw an error and the decoded email is different from the original,
    // it means the email was encoded. Return the original email as it's already encoded.
    // If the decoded email is the same as the input, it means the email was not encoded, so encode it.
    return decodedEmail === email ? encodeURIComponent(email) : email;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // If decoding fails, the email was not encoded, so encode it
    return encodeURIComponent(email);
  }
}

export const generateUniqueId = () => {
  // Current timestamp as the base for uniqueness
  const timestamp = Date.now().toString();
  // Generate random alphanumeric characters
  const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomPart = '';
  for (let i = 0; i < 18 - timestamp.length; i++) {
    randomPart += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
  }
  // Combine both parts to ensure the string is 18 characters long
  return (timestamp + randomPart).substring(0, 18);
};

export const truncateDescription = (description: string, maxLength: number) => {
  return description.length > maxLength ? `${description.substring(0, maxLength)}...` : description;
};

export function capitalizeWords(sentence: string) {
  if (!sentence) return '';

  return sentence
    .split(' ')
    .map((word) => {
      // capitalize only if the word doesn't contain any number (this is to avoid capitalizing ids)
      return /\d/.test(word) ? word : capitalize(word);
    })
    .join(' ');
}

export const normalizeName = (name: string) => {
  // Replace multiple spaces with single space and trim
  return name.replace(/\s+/g, ' ').trim().toLowerCase();
};

export function maskCharactersWithDots(input: string, showTwoDots = false) {
  if (!input) return undefined;

  const lastFourCharacters = input.slice(-4);
  let maskedCharacters = input.slice(0, -4).replace(/./g, '•');

  // for input length less than 6 characters, show at least 2 dots
  if (input.length < 6 || showTwoDots) {
    maskedCharacters = '••';
  }
  return { maskedCharacters, lastFourCharacters, string: maskedCharacters + lastFourCharacters };
}

export const getDayWithSuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatButtonText = (buttonText: string) => {
  const lowercaseWords = [
    'a',
    'an',
    'the',
    'and',
    'but',
    'or',
    'nor',
    'for',
    'so',
    'yet',
    'at',
    'by',
    'for',
    'from',
    'in',
    'of',
    'on',
    'to',
    'up',
    'with',
  ];

  return buttonText
    .split(' ')
    .map((word, index, array) => {
      // If the word is in uppercase, return it as is
      if (word === word.toUpperCase()) {
        return word;
      }

      const wordInLowerCase = word.toLowerCase();
      // Capitalize if it's the first or last word, or not in the lowercase list
      if (index === 0 || index === array.length - 1 || !lowercaseWords.includes(wordInLowerCase)) {
        return wordInLowerCase.charAt(0).toUpperCase() + wordInLowerCase.slice(1);
      }
      return word;
    })
    .join(' ');
};

export const limitTo3DecimalPlaces = (input: string) => {
  const parts = input.split('.');
  const updatedInput = parts[0] + '.' + parts[1]?.substring(0, 3);
  return updatedInput;
};

// Helper function to format decimal numbers and fix floating point precision issues
export const formatDecimal = (value: number, decimals: number = 2): string => {
  // Round to fix floating point precision issues and format to specified decimal places
  const rounded = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return rounded.toFixed(decimals);
};

/**
 * Extracts cursor parameter from a pagination URL string.
 */
export function extractCursorFromUrl(url: string): string | undefined {
  const match = url.match(/[?&]cursor=([^&]+)/);
  return match?.[1] ?? undefined;
}
